/**
 * Display-date formatter for the Daily page H1.
 *
 * Renders an ISO 8601 `YYYY-MM-DD` string as a long-form English
 * date with an ordinal day suffix (`"st"`, `"nd"`, `"rd"`, `"th"`)
 * — e.g., `"June 10th, 2026"`.
 *
 * Pure, deterministic, UTC-anchored. The Daily page H1 wraps this
 * in `"DVGUP for {date}"`; standalone usage (e.g., DayPage) renders
 * just the date.
 */

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

/**
 * Day-of-month → ordinal suffix mapping. The 11th, 12th, 13th
 * exceptions override the trailing-digit rule.
 */
function ordinalSuffix(day: number): string {
  if (day >= 11 && day <= 13) return 'th';
  switch (day % 10) {
    case 1:  return 'st';
    case 2:  return 'nd';
    case 3:  return 'rd';
    default: return 'th';
  }
}

/**
 * Format an ISO `YYYY-MM-DD` date as `"<Month> <day><ordinal>, <year>"`.
 *
 * Returns the input unchanged when it can't be parsed so callers
 * always have a non-empty string to render.
 */
export function formatDisplayDate(isoDate: string): string {
  if (!isoDate || typeof isoDate !== 'string') return isoDate ?? '';
  const parsed = new Date(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return isoDate;
  const month = MONTH_NAMES[parsed.getUTCMonth()];
  const day = parsed.getUTCDate();
  const year = parsed.getUTCFullYear();
  return `${month} ${day}${ordinalSuffix(day)}, ${year}`;
}

/**
 * Same as {@link formatDisplayDate} but omits the year when it
 * matches the current year — `"June 11th"` for today's year, and
 * `"June 11th, 2025"` for any other year.
 *
 * Used by the Daily page H1 ("DVGUP for June 11th") and the Days
 * page row labels so the year doesn't visually clutter the
 * common-case render. Anchored on `clockMs` (defaults to
 * `Date.now()`) so tests can pin the comparison year.
 */
export function formatDisplayDateSmartYear(
  isoDate: string,
  clockMs: number = Date.now(),
): string {
  if (!isoDate || typeof isoDate !== 'string') return isoDate ?? '';
  const parsed = new Date(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return isoDate;
  const month = MONTH_NAMES[parsed.getUTCMonth()];
  const day = parsed.getUTCDate();
  const year = parsed.getUTCFullYear();
  const currentYear = new Date(clockMs).getUTCFullYear();
  const suffix = ordinalSuffix(day);
  if (year === currentYear) return `${month} ${day}${suffix}`;
  return `${month} ${day}${suffix}, ${year}`;
}
